import { PASSWORD_POLICY } from '@volee/config'
import { FormField, Input } from '@volee/ui'
import type { UseFormRegisterReturn } from 'react-hook-form'

import { interpolate, messages } from '../messages/index.ts'

// The password inputs, shared by the three flows that ask for one. Presentation only: the
// state belongs to react-hook-form and the placement of errors to data/auth-fields.ts.

export interface PasswordFieldProps {
  readonly label: string
  readonly registration: UseFormRegisterReturn
  readonly error?: string
  readonly pending: boolean
  /** What the browser's password manager is being told. Not decoration: it is the feature. */
  readonly autoComplete: 'current-password' | 'new-password'
  readonly description?: string
}

export function PasswordField({
  label,
  registration,
  error,
  pending,
  autoComplete,
  description,
}: PasswordFieldProps) {
  return (
    <FormField label={label} description={description} error={error} required>
      {(props) => (
        <Input
          {...props}
          {...registration}
          type="password"
          autoComplete={autoComplete}
          disabled={pending}
        />
      )}
    </FormField>
  )
}

export interface NewPasswordFieldsProps {
  readonly newPassword: UseFormRegisterReturn
  readonly confirmPassword: UseFormRegisterReturn
  readonly errors: { readonly newPassword?: string; readonly confirmPassword?: string }
  readonly pending: boolean
}

/** The pair. The hint mirrors the pool's policy through @volee/config; Cognito validates. */
export function NewPasswordFields({
  newPassword,
  confirmPassword,
  errors,
  pending,
}: NewPasswordFieldsProps) {
  return (
    <>
      <PasswordField
        label={messages.password.new}
        description={interpolate(messages.password.policyHint, {
          minLength: PASSWORD_POLICY.minLength,
        })}
        registration={newPassword}
        error={errors.newPassword}
        pending={pending}
        autoComplete="new-password"
      />
      <PasswordField
        label={messages.password.confirm}
        registration={confirmPassword}
        error={errors.confirmPassword}
        pending={pending}
        autoComplete="new-password"
      />
    </>
  )
}
